import { useAtomValue } from "jotai";
import { useState, useEffect, useRef } from "react";
import { Button, Input } from "reactstrap";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import { IsMobileAtom } from "../../../App";
import classNames from "classnames";
import AppSpinner from "../../../elements/AppSpinner";
import QuickNoteModal from "../../Sidebar/QuickNote/QuickNoteModal";

interface SearchProps {
  disabled?: boolean;
  onChange: (s: string) => void;
  onLoading?: (b: boolean) => void;
  onClear?: () => void;
  clearFilter?: () => void;
  existingValue?: string;
  alwaysOpen?: boolean;
  width?: number;
  block?: boolean;
  searchTime?: number;
  size?: "sm" | "lg" | "md";
  css?: string;
}

//TODO: Add autofocus
const Search = ({
  disabled = false,
  onChange,
  onLoading,
  onClear,
  clearFilter,
  existingValue = "",
  alwaysOpen = false, // If true, the Search Input will always be open,
  width,
  block = false,
  searchTime = 1100,
  size = "sm",
  css = "",
}: SearchProps) => {
  const path = window.location.pathname;
  const isPublicForm = path.startsWith("/forms");
  const isSharedScreen = path.startsWith("/shared-");
  const mobileScreen = useAtomValue(IsMobileAtom);
  const [searchMode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState<null | NodeJS.Timeout>(
    null
  );
  const searchRef = useRef<any>(null);
  const [useVoice, setUseVoice] = useState(false);

  const [value, setValue] = useState("");

  useEffect(() => {
    setValue(existingValue);
    setLoading(false);

    if (!existingValue) {
      setSearchMode(false);
    }
  }, [existingValue]);

  const onInputChange = (val: string) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
      setSearchTimeout(null);
    }

    if (!val) callFunctionIfExists(clearFilter);

    setValue(val);
    setLoading(true);

    callFunctionIfExists(onLoading, true);

    const t = setTimeout(() => {
      if (val?.length === 0 || val?.length >= 1) {
        callFunctionIfExists(onChange, val);
        callFunctionIfExists(onLoading, false);

        setLoading(false);
      }

      setSearchTimeout(null);
    }, searchTime);

    setSearchTimeout(t);
  };

  const handleSearchDictation = () => {
    if (disabled) return;
    setUseVoice(true);
    setLoading(true);
  };

  const handleTranscriptSuccess = (value: string) => {
    const finalValue = value
      .replace(/[^a-zA-Z0-9\s]/g, "")
      .trim()
      .toLowerCase();

    setLoading(false);
    setValue(finalValue);
    onInputChange(finalValue);
  };

  const handleCloseSearchDictation = () => {
    setLoading(false);
    setUseVoice(false);
  };

  return (
    <>
      {searchMode || alwaysOpen ? (
        <div
          className={classNames({
            "d-inline-block": !block,
            "m-0": mobileScreen,
            "ml-1": !mobileScreen,
            [css]: !!css,
          })}
          style={{ position: "relative" }}
        >
          <Input
            id="search-bar-component"
            style={{
              width: width ? width : block ? "100%" : 200,
              paddingRight: 25,
            }}
            ref={searchRef}
            bsSize={size as "sm" | "lg" | undefined}
            type="text"
            onChange={(e) => onInputChange(e.target.value)}
            placeholder="Search"
            value={value}
            className={mobileScreen ? "search-input" : ""}
            disabled={disabled}
          />
          {!alwaysOpen || value?.length || loading ? (
            <div
              className="h-100"
              style={{
                position: "absolute",
                right: 4,
                top: 0,
              }}
              onClick={() => {
                if (loading) return;

                setValue("");
                setSearchMode(false);

                callFunctionIfExists(onClear);
                callFunctionIfExists(clearFilter);

                if (searchTimeout) {
                  clearTimeout(searchTimeout);

                  setSearchTimeout(null);
                }

                callFunctionIfExists(onChange, "");
                callFunctionIfExists(onLoading, false);
              }}
            >
              {loading ? (
                <AppSpinner size="sm" />
              ) : (
                <i
                  className="mdi mdi-close cursor-pointer text-danger"
                  style={{
                    fontSize: size === "sm" ? "22px" : "80px",
                  }}
                />
              )}
            </div>
          ) : (
            <>
              {/* Dictation not supported in public forms. */}
              {!isPublicForm && !isSharedScreen && (
                <div
                  className="h-100"
                  style={{
                    position: "absolute",
                    right: 3,
                    top: 0,
                  }}
                  onClick={handleSearchDictation}
                >
                  <i
                    className={classNames(
                      "mdi mdi-microphone cursor-pointer text-primary",
                      disabled && "cursor-not-allowed"
                    )}
                    style={{
                      fontSize: size === "sm" ? "22px" : "80px",
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <Button
          disabled={disabled}
          size="sm"
          color="secondary"
          outline
          className={
            mobileScreen
              ? "d-flex justify-content-center align-items-center mx-2"
              : "mx-2"
          }
          style={{ width: mobileScreen ? 100 : undefined }}
          onClick={() => {
            setSearchMode(true);

            setTimeout(() => {
              searchRef?.current?.focus?.();
            }, 0);
          }}
        >
          <i
            className="mdi mdi-magnify mr-1"
            style={{
              fontSize: 16,
              lineHeight: !mobileScreen ? 1 : undefined,
              top: !mobileScreen ? 2 : undefined,
              position: !mobileScreen ? "relative" : undefined,
            }}
          ></i>
          <span>Search</span>
        </Button>
      )}
      {useVoice && (
        // @ts-ignore
        <QuickNoteModal
          open={useVoice}
          setOpen={handleCloseSearchDictation}
          header="Brie Dictation"
          useBrieDication={true}
          saveTranscription={handleTranscriptSuccess}
          body="Click to Start Dictating"
          initializeRecording={true}
          instantSave={true}
          showSuccess={false}
        />
      )}
    </>
  );
};

export default Search;
